import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, UserPlus } from "lucide-react";
import { hgLink, trackOutbound, trackConversion, HG_TRIAL_PATH, LOCATIONS } from "../data/site";
import { getAttribution, newSubmissionId } from "../lib/attribution";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("mma_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("mma_chat_session", id);
  }
  return id;
};

const QUICK_PROMPTS = [
  "I'm a total beginner — where do I start?",
  "What classes are good for kids?",
  "What should I bring to my first class?",
];

const ChatLeadForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", location: "richardson", sms_consent: false, email_consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const subIdRef = useRef(newSubmissionId());
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/leads/private-lesson`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...f,
          kind: "chat",
          goal: "FREE TRIAL REQUEST: Trial Assistant chat handoff",
          submission_id: subIdRef.current,
          attribution: getAttribution(),
          utm_source: "chat-assistant",
          utm_campaign: "chat-handoff",
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || "failed");
      trackConversion("generate_lead", { lead_type: "chat", location: f.location, transaction_id: subIdRef.current });
      onDone(true);
    } catch (err) {
      trackConversion("form_error", { form: "chat", code: "submit_failed" });
      onDone(false, typeof err.message === "string" && err.message !== "failed" ? err.message : null);
    }
    setBusy(false);
  };
  const inp = "w-full bg-white/10 border border-white/15 text-white text-sm px-3 py-2 placeholder:text-white/35 focus:outline-none focus:border-white/50";
  return (
    <form onSubmit={submit} className="space-y-2 border border-white/15 p-3" data-testid="chat-lead-form">
      <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Have us contact you</p>
      <input required value={f.name} onChange={set("name")} placeholder="Name" className={inp} data-testid="chat-lead-name-input" />
      <div className="grid grid-cols-2 gap-2">
        <input required type="tel" value={f.phone} onChange={set("phone")} placeholder="Phone" className={inp} data-testid="chat-lead-phone-input" />
        <input required type="email" value={f.email} onChange={set("email")} placeholder="Email" className={inp} data-testid="chat-lead-email-input" />
      </div>
      <select value={f.location} onChange={set("location")} className={inp} data-testid="chat-lead-location-select">
        {LOCATIONS.map((l) => <option key={l.slug} value={l.slug} className="text-black">{l.short}</option>)}
        <option value="either" className="text-black">Either works</option>
      </select>
      <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer">
        <input type="checkbox" checked={f.sms_consent} onChange={set("sms_consent")} className="mt-0.5" data-testid="chat-lead-sms-consent" />
        OK to text me (SMS)
      </label>
      <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer">
        <input type="checkbox" checked={f.email_consent} onChange={set("email_consent")} className="mt-0.5" data-testid="chat-lead-email-consent" />
        OK to email me updates
      </label>
      <button type="submit" disabled={busy} data-testid="chat-lead-submit-btn" className="w-full bg-white text-[#0A0B0D] font-mono text-xs uppercase tracking-widest py-2.5 disabled:opacity-40 hover:bg-neutral-200 transition-colors">
        {busy ? "Sending…" : "Request a Call Back"}
      </button>
    </form>
  );
};

const Bubble = ({ role, children }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      data-testid={`chat-message-${role}`}
      className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        role === "user" ? "bg-white text-[#0A0B0D]" : "bg-white/10 text-white border border-white/10"
      }`}
    >
      {children}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadMode, setLeadMode] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const scrollRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/history/${getSessionId()}`)
      .then((r) => r.json())
      .then((h) => Array.isArray(h) && h.length && setMessages(h))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    if (!startedRef.current) {
      startedRef.current = true;
      trackConversion("chat_started", { source: "trial_assistant" });
    }
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta" || ev.type === "error") {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + ev.content };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again or call (855) 204-0004." };
        return next;
      });
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Chat with the Trial Assistant"
          data-testid="chat-widget-open-btn"
          className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-[60] flex items-center gap-2 bg-[#0A0B0D] text-white border border-white/25 px-4 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-[#0A0B0D] hover:border-[#0A0B0D] transition-colors shadow-xl"
        >
          <MessageSquare size={16} />
          <span className="hidden sm:inline">Questions?</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-20 lg:bottom-6 right-2 lg:right-6 z-[60] w-[min(94vw,380px)] h-[min(72vh,540px)] bg-[#0A0B0D] border border-white/20 shadow-2xl flex flex-col grain"
          role="dialog"
          aria-label="Trial Assistant chat"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/15">
            <div>
              <p className="font-display font-black uppercase text-white text-sm tracking-wide">Trial Assistant</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Mezger Martial Arts · AI</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-widget-close-btn" className="text-white/60 hover:text-white p-1">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
            {messages.length === 0 && (
              <>
                <Bubble role="assistant">Hey! I can answer anything about classes, the schedule, our two locations, or what your first day looks like. What would you like to know?</Bubble>
                <div className="flex flex-col gap-2 pt-1">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q} onClick={() => send(q)} data-testid="chat-quick-prompt" className="text-left text-xs text-white/70 border border-white/15 px-3 py-2 hover:bg-white/10 hover:text-white transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role}>{m.content || (busy && i === messages.length - 1 ? "…" : m.content)}</Bubble>
            ))}
            {leadMode && !leadDone && (
              <ChatLeadForm onDone={(ok, msg) => {
                if (ok) {
                  setLeadDone(true);
                  setLeadMode(false);
                  setMessages((m) => [...m, { role: "assistant", content: "Got it — Hidden Gym staff will reach out shortly to set up your visit. Anything else I can answer meanwhile?" }]);
                } else {
                  setMessages((m) => [...m, { role: "assistant", content: msg || "That didn't go through — mind trying again, or call (855) 204-0004?" }]);
                }
              }} />
            )}
          </div>

          <div className="border-t border-white/15 p-3 space-y-2">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about classes, schedule…"
                maxLength={1500}
                data-testid="chat-input"
                className="flex-1 bg-white/10 border border-white/15 text-white text-sm px-3 py-2.5 placeholder:text-white/35 focus:outline-none focus:border-white/50"
              />
              <button type="submit" disabled={busy} aria-label="Send message" data-testid="chat-send-btn" className="bg-white text-[#0A0B0D] px-3.5 disabled:opacity-40 hover:opacity-85 transition-opacity">
                <Send size={16} />
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {!leadDone && (
                <button type="button" onClick={() => setLeadMode((v) => !v)} data-testid="chat-lead-toggle-btn"
                  className="flex items-center justify-center gap-1.5 border border-white/25 text-white font-mono text-[11px] uppercase tracking-widest py-2.5 hover:bg-white/10 transition-colors">
                  <UserPlus size={13} /> {leadMode ? "Hide Form" : "Contact Me"}
                </button>
              )}
              <Link
                to={hgLink(HG_TRIAL_PATH, "chat-widget")}
                onClick={() => trackOutbound("chat-widget", "chat-trial-cta")}
                data-testid="chat-trial-cta"
                className={`block text-center bg-white text-[#0A0B0D] font-mono text-[11px] uppercase tracking-widest py-2.5 hover:bg-neutral-200 transition-colors ${leadDone ? "col-span-2" : ""}`}
              >
                Book a Free Trial →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
